import {
  type ClassTypeWithPropertyType,
  PropertyType,
  isGradientClassByPattern,
  isBackgroundImageClassByPattern,
} from "@/tailwind/tailwindUtils";
import type { ComboBoxOptionType } from "../components/input";
import { classMatchedPropertyType } from "../../usePropertyStateV2";

const GRADIENT_DIRECTION_PREFIX = "bg-gradient-";
const DEFAULT_GRADIENT_DIRECTION = "bg-gradient-to-r";
const GRADIENT_PROPERTY_TYPES = [
  PropertyType.FROM_COLOR,
  PropertyType.TO_COLOR,
  PropertyType.VIA_COLOR,
  PropertyType.FROM_POSITION,
  PropertyType.TO_POSITION,
  PropertyType.VIA_POSITION,
];

export const safeParseCssStringAsInlineStyles = (cssString: string) => {
  try {
    const cssObject = {};
    const lines = cssString.split(";");

    for (const line of lines) {
      const [key, value] = line.split(":");
      if (key && value) {
        const formattedKey = key
          .trim()
          .replace(/-./g, (match) => match[1].toUpperCase());
        cssObject[formattedKey] = value.trim();
      }
    }
    return cssObject;
  } catch (error) {
    return {};
  }
};

const justifyContentMap = ["flex-start", "center", "flex-end"];
const alignItemsMap = ["flex-start", "center", "flex-end"];

export const modifyForReverse = (direction: string, alignment: string) => {
  if (direction?.endsWith("reverse")) {
    const reverseMap = {
      "flex-start": "flex-end",
      "flex-end": "flex-start",
      center: "center",
    };
    return reverseMap[alignment];
  }
  return alignment;
};

export const getIndexFromStyles = (
  direction: string,
  justifyContent: string,
  alignItems: string,
) => {
  if (alignItems === "start") {
    alignItems = "flex-start";
  } else if (alignItems === "end") {
    alignItems = "flex-end";
  }

  if (justifyContent === "start") {
    justifyContent = "flex-start";
  } else if (justifyContent === "end") {
    justifyContent = "flex-end";
  }

  justifyContent = modifyForReverse(direction, justifyContent);

  const justifyContentIndex = justifyContentMap.indexOf(justifyContent);
  const alignItemsIndex = alignItemsMap.indexOf(alignItems);

  if (justifyContentIndex === -1 || alignItemsIndex === -1) {
    return -1;
  }

  let index: number;
  if (!direction || direction.startsWith("row")) {
    index = alignItemsIndex * 3 + justifyContentIndex;
  } else {
    index = justifyContentIndex * 3 + alignItemsIndex;
  }

  return index;
};

export const getStylesFromIndex = (direction: string, index: number) => {
  let justifyContent: string;
  let alignItems: string;

  if (direction.startsWith("row")) {
    justifyContent = justifyContentMap[index % 3];
    alignItems = alignItemsMap[Math.floor(index / 3)];
  } else {
    justifyContent = justifyContentMap[Math.floor(index / 3)];
    alignItems = alignItemsMap[index % 3];
  }

  justifyContent = modifyForReverse(direction, justifyContent);

  return { justifyContent, alignItems };
};

const getClassVariant = (cls: string): string => {
  return cls.includes(":") ? cls.split(":")[0] : "default";
};

export const calculateNewClasses = ({
  option,
  propertyType,
  oldClasses,
  tailwindClassesMap,
  variant,
}: {
  option?: ComboBoxOptionType | null;
  propertyType: PropertyType;
  oldClasses: string[];
  tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;
  variant?: string | null;
}) => {
  const newClasses: string[] = [];
  let foundMatchingClass = false;
  const targetVariant = variant || "default";

  const isSettingGradientProperty =
    GRADIENT_PROPERTY_TYPES.includes(propertyType);

  // Determine what to clear based on property type
  // BACKGROUND_COLOR should clear gradients and images of the same variant
  // BACKGROUND_IMAGE should clear other gradients/images of same variant
  // Gradient color properties (FROM_COLOR, TO_COLOR, VIA_COLOR) should NOT clear gradient directions
  // Clear gradients when setting BACKGROUND_COLOR or ANY BACKGROUND_IMAGE (including gradient directions)
  const shouldClearGradients =
    propertyType === PropertyType.BACKGROUND_COLOR ||
    propertyType === PropertyType.BACKGROUND_IMAGE;

  const shouldClearImages =
    propertyType === PropertyType.BACKGROUND_COLOR ||
    propertyType === PropertyType.BACKGROUND_IMAGE;

  // Process each existing class
  for (let i = 0; i < oldClasses.length; i++) {
    const className = oldClasses[i];
    const classVariant = getClassVariant(className);

    // Handle gradient classes
    if (isGradientClassByPattern(className)) {
      // When setting a solid color or new gradient, clear existing gradients of the same variant
      if (shouldClearGradients && classVariant === targetVariant) {
        // Skip this gradient class - it will be replaced
        continue;
      }

      // Additional check: if we're setting a gradient property and this gradient class
      // matches the same property type and variant, skip it to prevent duplicates
      // Only apply this for non-BACKGROUND_IMAGE types to avoid interfering with existing logic
      if (
        isSettingGradientProperty &&
        propertyType !== PropertyType.BACKGROUND_IMAGE &&
        classVariant === targetVariant
      ) {
        const gradientMatchesProperty = classMatchedPropertyType({
          className,
          propertyType,
          tailwindClassMap: tailwindClassesMap,
          variant,
        });

        if (gradientMatchesProperty) {
          // Skip this gradient class as we're replacing it with a new one of the same type
          continue;
        }
      }

      // Otherwise preserve gradients from other variants
      newClasses.push(className);
      continue;
    }

    // Handle background image URLs
    if (isBackgroundImageClassByPattern(className)) {
      if (shouldClearImages && classVariant === targetVariant) {
        // Skip existing images when setting a background color or new image of the same variant
        continue;
      }
      // Preserve images from other variants
      newClasses.push(className);
      continue;
    }

    // Special handling: when clearing gradient properties, also clear background colors
    if (isSettingGradientProperty && !option) {
      // Check if this is a background color that should be cleared
      const isBgColor = classMatchedPropertyType({
        className,
        propertyType: PropertyType.BACKGROUND_COLOR,
        tailwindClassMap: tailwindClassesMap,
        variant,
      });

      if (isBgColor && classVariant === targetVariant) {
        // Skip background colors when clearing gradient properties
        continue;
      }
    }

    // Check if this class matches our property type and variant
    const matchesProperty = classMatchedPropertyType({
      className,
      propertyType,
      tailwindClassMap: tailwindClassesMap,
      variant,
    });

    if (matchesProperty) {
      // This class matches both the property type AND the target variant
      // For background colors with design tokens, clear ALL matching classes of the same variant
      if (
        propertyType === PropertyType.BACKGROUND_COLOR &&
        classVariant === targetVariant
      ) {
        // Replace all design token background colors with the new one
        if (!foundMatchingClass && option) {
          foundMatchingClass = true;
          const newClass =
            targetVariant !== "default"
              ? `${targetVariant}:${option.tailwind}`
              : option.tailwind;
          newClasses.push(newClass);
        }
        // Skip the old class (it's being replaced)
      } else if (classVariant === targetVariant) {
        // For non-background properties, use standard replacement
        if (!foundMatchingClass && option) {
          foundMatchingClass = true;
          const newClass =
            targetVariant !== "default"
              ? `${targetVariant}:${option.tailwind}`
              : option.tailwind;
          newClasses.push(newClass);
        }
        // Skip ALL old classes that match this property type and variant
        // This prevents duplicates like "from-amber-500 from-fuchsia-700"
      } else {
        // Keep classes from other variants (hover:, focus:, lg:, etc.)
        newClasses.push(className);
      }
    } else {
      // Keep classes that don't match the property type
      newClasses.push(className);
    }
  }

  // If no matching class was found, append the new option
  if (!foundMatchingClass && option) {
    const newClass =
      targetVariant !== "default"
        ? `${targetVariant}:${option.tailwind}`
        : option.tailwind;
    newClasses.push(newClass);
  }

  // Check if we're adding a gradient color through the option.
  // we need to add the default direction if we are adding a gradient color and there is no direction
  const isAddingGradientColor =
    option && GRADIENT_PROPERTY_TYPES.includes(propertyType);
  // After all processing, check if we have gradient colors but no direction

  const hasGradientDirection = newClasses.some((cls) => {
    const baseClass = cls.includes(":") ? cls.split(":").pop() || cls : cls;
    return baseClass.startsWith(GRADIENT_DIRECTION_PREFIX);
  });

  // Add default direction if we have gradient colors but no direction
  if (isAddingGradientColor && !hasGradientDirection) {
    // Find the variant of the gradient color to match it
    const gradientColorClass = newClasses.find((cls) => {
      const baseClass = cls.includes(":") ? cls.split(":").pop() || cls : cls;
      return /^(from-|to-|via-)/.test(baseClass);
    });

    // Use the variant from the gradient color or target variant
    let variantToUse = targetVariant;
    if (gradientColorClass?.includes(":")) {
      variantToUse = gradientColorClass.split(":")[0];
    }

    if (variantToUse && variantToUse !== "default") {
      newClasses.unshift(`${variantToUse}:${DEFAULT_GRADIENT_DIRECTION}`);
    } else {
      newClasses.unshift(DEFAULT_GRADIENT_DIRECTION);
    }
  }

  return newClasses;
};

export function sortTailwindColorOptions<T extends { tailwind: string }>(
  options: T[],
): T[] {
  return [...options].sort((a, b) => {
    const [aBase, aOpacity] = a.tailwind.split("/");
    const [bBase, bOpacity] = b.tailwind.split("/");
    if (aBase < bBase) return -1;
    if (aBase > bBase) return 1;

    const aOp = aOpacity ? Number.parseInt(aOpacity) : 100;
    const bOp = bOpacity ? Number.parseInt(bOpacity) : 100;

    return bOp - aOp;
  });
}

export const extractVariant = (
  classStr: string,
  defaultVariant?: string | null,
) => {
  if (defaultVariant || !classStr.includes(":")) {
    return { variant: defaultVariant, className: classStr };
  }
  const [variant, className] = classStr.split(":", 2);
  return { variant, className };
};

export const areClassesUnchanged = (
  newClasses: string[],
  elementClasses: string[],
) => {
  return (
    newClasses.length === elementClasses.length &&
    newClasses.every((cls, i) => cls === elementClasses[i])
  );
};
